import { Injectable, inject } from '@angular/core';
import { AnalyticsProduct } from '@warema/shared/models';
import { brochuresFilters, BrochureItem } from '@warema/shared/shop/brochures/models';
import { CartItem, CartPosition, ProductType } from '@warema/shared/shop/cart/models';
import { filters as fabricFilters, FabricItem } from '@warema/shared/shop/fabrics/models';
import { FilterType } from '@warema/shared/shop/shared/filters/models';
import { SlatItem } from '@warema/shared/shop/slats/models';
import { GoogleTrackingService } from '../google-tracking.service';

@Injectable({
    providedIn: 'root',
})
export class ShopTrackingService {
    private readonly _googleTrackingService = inject(GoogleTrackingService);

    private readonly _brandName = 'Warema';

    public addToCart(item: CartItem, positionInPage: number): void {
        const itemProduct = this._getProduct(item, positionInPage);
        if (!itemProduct) {
            console.warn('addToCart: cant get Product', item, positionInPage);
            return;
        }

        this._googleTrackingService.track({
            event: 'addToCart',
            ecommerce: {
                add: {
                    products: [itemProduct],
                },
            },
        });
    }

    public removeFromCart(item: CartItem, positionInPage: number): void {
        const itemProduct = this._getProduct(item, positionInPage);
        if (!itemProduct) {
            console.warn('removeFromCart: cant get Product', item, positionInPage);
            return;
        }

        this._googleTrackingService.track({
            event: 'removeFromCart',
            ecommerce: {
                remove: {
                    products: [itemProduct],
                },
            },
        });
    }

    public addSlatToFavourites(item: SlatItem): void {
        this._googleTrackingService.track({
            event: 'addFavorite',
            ecommerce: {
                add: {
                    products: [this._getSlatProduct(item, 1)],
                },
            },
        });
    }

    public addBrochureToFavourites(item: BrochureItem, positionInPage: number): void {
        this._googleTrackingService.track({
            event: 'addFavorite',
            ecommerce: {
                add: {
                    products: [this._getBrochureProduct(item, positionInPage)],
                },
            },
        });
    }

    public downloadBrochure(item: BrochureItem, positionInPage: number): void {
        this._googleTrackingService.track({
            event: 'fileDownload',
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            fileExtension: item.pdfName?.split('.').pop()?.toLowerCase() || 'pdf',
            fileName: item.pdfName,
            ecommerce: {
                add: {
                    products: [this._getBrochureProduct(item, positionInPage)],
                },
            },
        });
    }

    public addFabricsToFavourites(item: FabricItem, positionInPage: number): void {
        this._googleTrackingService.track({
            event: 'addFavorite',
            ecommerce: {
                add: {
                    products: [this._getFabricProduct(item, positionInPage)],
                },
            },
        });
    }

    public removeSlatFromFavourites(item: SlatItem): void {
        this._googleTrackingService.track({
            event: 'removeFavorite',
            ecommerce: {
                remove: {
                    products: [this._getSlatProduct(item, 1)],
                },
            },
        });
    }

    public removeBrochureFromFavourites(item: BrochureItem, positionInPage: number): void {
        this._googleTrackingService.track({
            event: 'removeFavorite',
            ecommerce: {
                remove: {
                    products: [this._getBrochureProduct(item, positionInPage)],
                },
            },
        });
    }

    public removeFabricFromFavourites(item: FabricItem, positionInPage: number): void {
        this._googleTrackingService.track({
            event: 'removeFavorite',
            ecommerce: {
                remove: {
                    products: [this._getFabricProduct(item, positionInPage)],
                },
            },
        });
    }

    public trackShopOrder(
        step: number,
        positions: Array<CartPosition>,
        consultationId: string | undefined = undefined,
    ): void {
        const products = positions.map((product, position) => this._getProduct(product.item, position + 1));

        const jsonObject = {
            event: 'checkout',
            ecommerce: {
                checkout: {
                    actionField: {
                        step,
                        consultationId: undefined as string | undefined,
                    },
                    products,
                },
            },
            promoView: {
                promotions: [
                    {
                        id: '0',
                        name: '0',
                        creative: '0',
                        position: '0',
                    },
                ],
            },
        };

        if (consultationId !== undefined) {
            jsonObject.ecommerce.checkout.actionField['consultationId'] = consultationId;
        }

        this._googleTrackingService.track(jsonObject);
    }

    public trackFinalizeOrder(
        id: string,
        positions: Array<CartPosition>,
        email: string,
        consultationId: string | undefined = undefined,
    ): void {
        const products = positions
            .map((product, position) => this._getProduct(product.item, position + 1))
            .filter(x => x != null);

        const jsonObject = {
            event: 'transaction',
            businessUnit: this._getBusinessUnitValue(positions, products),
            ecommerce: {
                purchase: {
                    actionField: {
                        id,
                        affiliation: 'Warema Plus Shop',
                        revenue: products
                            .map(x => Number.parseInt(x.price, 10))
                            .reduce((prev, curr) => prev + curr, 0)
                            .toFixed(2),
                        tax: '0',
                        shipping: '0',
                        email,
                        consultationId: undefined as string | undefined,
                    },
                    products,
                },
            },
        };

        if (consultationId !== undefined) {
            jsonObject.ecommerce.purchase.actionField['consultationId'] = consultationId;
        }

        this._googleTrackingService.track(jsonObject);
    }

    public trackOpenFaq(): void {
        this._googleTrackingService.track({
            event: 'itemClick',
            itemClick: 'FAQ flag',
        });
    }

    private _getProduct(item: CartItem, positionInPage: number): AnalyticsProduct | undefined {
        switch (item.productType) {
            case ProductType.BROCHURE:
                return this._getBrochureProduct(item.productDetails as BrochureItem, positionInPage);
            case ProductType.FABRIC:
                return this._getFabricProduct(item.productDetails as FabricItem, positionInPage);
            case ProductType.SLAT:
                return this._getSlatProduct(item.productDetails as SlatItem, positionInPage);
        }
        return undefined;
    }

    private _getFabricProduct(item: FabricItem, positionInPage: number): AnalyticsProduct {
        return {
            id: item.articleNr,
            name: item.name,
            price: '1.00',
            brand: this._brandName,
            category: this._getAnalyticsValue(item.productFamilies, ProductType.FABRIC, FilterType.productFamilies),
            variant: this._getAnalyticsValue(item.colors, ProductType.FABRIC, FilterType.colors),
            dimension1: this._getAnalyticsValue([item.muster], ProductType.FABRIC, FilterType.muster),
            position: positionInPage,
            quantity: '1',
        };
    }

    private _getBrochureProduct(item: BrochureItem, positionInPage: number): AnalyticsProduct {
        return {
            id: item.articleNr,
            name: item.name,
            price: '1.00',
            brand: this._brandName,
            category: this._getAnalyticsValue(item.productFamilies, ProductType.BROCHURE, FilterType.productFamilies),
            variant: '',
            dimension1: '',
            position: positionInPage,
            quantity: '1',
        };
    }

    private _getSlatProduct(item: SlatItem, positionInPage: number): AnalyticsProduct {
        return {
            id: item.articleNr,
            name: item.name,
            price: '1.00',
            brand: this._brandName,
            category: '',
            variant: '',
            dimension1: '',
            position: positionInPage,
            quantity: '1',
        };
    }

    private _getAnalyticsValue(ids: Array<string>, productType: ProductType, filterType: FilterType): string {
        const filterItem = productType === ProductType.BROCHURE ? brochuresFilters : fabricFilters;

        return filterItem
            .filter(x => ids.includes(x.id) && x.filterType === filterType)
            .map(x => x.analyticsValue)
            .toString();
    }

    private _getBusinessUnitValue(
        cartPositions: Array<CartPosition>,
        analyticsProducts: Array<AnalyticsProduct>,
    ): string | undefined {
        const hasFabric = cartPositions.some(x => x.item.productType === ProductType.FABRIC);
        const hasSlat = cartPositions.some(x => x.item.productType === ProductType.SLAT);

        const brochureItemArticleNumbers = new Set(
            cartPositions.filter(x => x.item.productType === ProductType.BROCHURE).map(x => x.item.articleNr),
        );

        const analyticBrochures = analyticsProducts.filter(x => brochureItemArticleNumbers.has(x.id));

        // We can use the german category names, because the analytics values are allways german
        const hasPatio = analyticBrochures.some(
            x => x.category.includes('Terrasse') || x.category.includes('Garten & Wintergarten'),
        );
        const hasWindow = analyticBrochures.some(x => x.category.includes('Fenster & Fassade'));

        if ((hasPatio || hasFabric) && (hasWindow || hasSlat)) {
            return 'Outdoor Living, Window Systems';
        } else if (hasPatio || hasFabric) {
            return 'Outdoor Living';
        } else if (hasWindow || hasSlat) {
            return 'Window Systems';
        }

        return undefined;
    }
}
